import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { BRAND } from "./auth/authTheme";

const formatDate = (value) => {
  if (!value) return "";
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return "";
  }
};

const menuItemStyle = {
  px: 1.5,
  py: 1.25,
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background 0.2s ease",
  "&:hover": {
    backgroundColor: BRAND.bg,
  },
};

export default function NotificationDropdown({
  announcements = [],
  loading = false,
  canManage = false,
  onClose,
}) {
  const [expandedId, setExpandedId] = useState(null);

  const activeAnnouncements = useMemo(() => {
    return (announcements || [])
      .filter((item) => item.active !== false)
      .sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return bTime - aTime;
      });
  }, [announcements]);

  return (
    <Box className="notification-dropdown-panel">
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "15px" }}
        >
          Notifications
        </Typography>
        <Typography variant="caption" sx={{ color: BRAND.muted }}>
          {activeAnnouncements.length
            ? `${activeAnnouncements.length} active`
            : "No new updates"}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: BRAND.border }} />

      <Box
        sx={{
          maxHeight: 320,
          overflowY: "auto",
          py: 0.5,
          px: 0.75,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: "var(--brand-orange)" }} />
          </Box>
        ) : activeAnnouncements.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3.5, px: 2 }}>
            <CampaignOutlinedIcon
              sx={{ fontSize: 36, color: "#CBD5E1", mb: 0.5 }}
            />
            <Typography sx={{ fontWeight: 600, color: BRAND.navy, fontSize: "0.875rem" }}>
              No notifications yet
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted, mt: 0.25 }}>
              Announcements will appear here.
            </Typography>
          </Box>
        ) : (
          activeAnnouncements.map((item) => {
            const isExpanded = expandedId === item.id;
            return (
              <Box
                key={item.id}
                sx={menuItemStyle}
                onClick={() =>
                  setExpandedId(isExpanded ? null : item.id)
                }
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: BRAND.navy,
                    fontSize: "0.875rem",
                    mb: 0.25,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: BRAND.muted,
                    display: isExpanded ? "block" : "-webkit-box",
                    WebkitLineClamp: isExpanded ? "unset" : 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: isExpanded ? "pre-wrap" : "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {item.message}
                </Typography>
                {item.created_at && (
                  <Typography sx={{ fontSize: "0.6875rem", color: "#94A3B8", mt: 0.5 }}>
                    {formatDate(item.created_at)}
                  </Typography>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {canManage && (
        <>
          <Divider sx={{ borderColor: BRAND.border }} />
          <Box sx={{ p: 0.75 }}>
            <Box
              component={Link}
              to="/announcement"
              onClick={onClose}
              sx={{
                ...menuItemStyle,
                display: "flex",
                alignItems: "center",
                color: BRAND.text,
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: BRAND.orangeLight,
                  color: "var(--brand-orange)",
                },
              }}
            >
              <SettingsOutlinedIcon
                sx={{ mr: 1.5, fontSize: 20, color: "inherit" }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Manage announcements
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
